<template>
    <section class="contact-files">
        <div class="files-header">
            <h3>Files</h3>

            <div>
                <div class="new-file-upload">
                    <input
                        ref="fileUpload"
                        hidden
                        type="file"
                        @change="handleSelection"
                    />

                    <ds-filled-button
                        :loading="loading"
                        data-qa="attach-button"
                        @click="uploadFile"
                    >
                        Add file
                    </ds-filled-button>
                </div>

                <p class="fileupload-helptext">
                    10 MB limit
                </p>
            </div>
        </div>

        <contact-file-modal
            ref="fileModal"
            :contact="contact"
            :new-file-name="newFile.fileName"
            :new-file-size="newFileSizeInBytes"
            :original-file-size="originalFileSizeInBytes"
            data-qa="file-modal"
            @replaceOldFileWithNew="replaceOldFileWithNew('replaceFile')"
            @keepBothFiles="replaceOldFileWithNew('bothFiles')"
        />

        <template v-if="isContactHasFiles" lang="html">
            <div class="table-scroller">
                <ds-table-grid
                    ref="table"
                    :data="files"
                    align="center"
                    data-qa="files-list-table"
                    class="files-list-table"
                    :sort-field="sortField"
                    :sort-ascending="sortAscending"
                    @sort="sortTable"
                >
                    <ds-table-grid-column
                        label="File name"
                        prop="fileName"
                        width="350"
                        data-qa="rename-file"
                    >
                        <template slot-scope="item">
                            <ds-editable-text
                                v-model="item.fileName"
                                always-show-icon
                                class="rename-file"
                                :title="item.fileName"
                                @input="renameFile(item)"
                            >
                                <span class="filename">{{ item.fileName }}</span>
                            </ds-editable-text>
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="Size"
                        width="140"
                        prop="fileSize"
                    >
                        <template slot-scope="item">
                            <span> {{ item.fileSize }} </span>
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="Date uploaded"
                        width="250"
                        prop="dateCreated"
                        sortable
                    >
                        <template slot-scope="item">
                            <span> {{ item.dateCreated }} </span>
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="Download"
                        width="90"
                        prop="downloadURL"
                        data-qa="download-file"
                    >
                        <template slot-scope="item">
                            <div class="center delete">
                                <a class="download-file" :href="item.downloadURL"><ds-icon name="download" /></a>
                            </div>
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="Delete"
                        width="70"
                        prop="delete"
                        data-qa="remove-file"
                    >
                        <template slot-scope="item">
                            <div class="center trash" @click="removeFile(item.id)">
                                <ds-icon name="trash-2" />
                            </div>
                        </template>
                    </ds-table-grid-column>
                </ds-table-grid>
            </div>
        </template>

        <div v-if="!isContactHasFiles" data-qa="no-files-section" class="no-files-section">
            This Contact does not have any files
        </div>
    </section>
</template>

<script>
import axios from 'axios';
import ContactFileModal from './ContactFileModal.vue';

export default {
    components: {
        ContactFileModal,
    },

    props: {
        contact: Object,
    },

    data() {
        return {
            url: '/app/fileBox',
            files: [],
            sortField: 'dateCreated',
            sortAscending: false,
            uploadFileName: '',
            imageSrc: '',
            originalFileSizeInBytes: null,
            newFileSizeInBytes: null,
            newFile: {},
            oldFile: {},
            isDuplicate: false,
            inValidFileSize: false,
            loading: false,
        };
    },

    created() {
        this.loadContactFiles();
    },

    computed: {
        isContactHasFiles() {
            return Boolean(this.files?.length);
        },
    },

    methods: {
        async loadContactFiles() {
            try {
                const response = await axios.get(`${this.url}/list/${this.contact.id}`);

                this.files = response.data;
            } catch (error) {
                this.$error({ message: 'Unable to get files for this contact' });
            }
        },

        async duplicateFileName(file) {
            this.isDuplicate = false;
            await this.files?.filter((item) => {
                if (item?.fileName === file?.fileName && item?.id !== file?.id) {
                    this.isDuplicate = true;
                    this.oldFile = item;
                    this.originalFileSizeInBytes = item?.fileSize?.replace(' KB', '') * 1000;
                }

                return false;
            });

            if (this.isDuplicate) {
                this.$refs.fileModal.showModal(0);
            } else {
                this.$refs.fileModal.modalClose();
            }
        },

        uploadFile() {
            this.$refs.fileUpload.click();
        },

        async fileUpload() {
            const fileObj = {
                fileName: this.uploadFileName,
                fileData: this.imageSrc,
                contactId: this.contact.id,
                createdBy: this.userId,
            };

            try {
                const response = await axios.post(`${this.url}/uploadFile`, fileObj);

                this.newFile = response.data;
                this.newFileSizeInBytes = this.newFile?.fileSize?.replace(' KB', '') * 1000;
                this.loadContactFiles();
                this.duplicateFileName(response.data);

                if (!this.isDuplicate) {
                    this.loading = false;
                    this.$toast({ message: 'New file uploaded.' });
                }
                this.uploadFileName = '';
                this.imageSrc = '';
                this.$refs.fileUpload.value = '';
            } catch (error) {
                this.resetFields();
                this.$toast({ message: 'Error adding new file' });
            }
        },

        async removeFile(id) {
            try {
                await axios.delete(`${this.url}/deleteFile/${id}`);

                this.$toast({ message: 'File deleted' });
                this.loadContactFiles();
            } catch (e) {
                this.$error({ message: 'Unable to remove  Files' });
            }
        },

        async renameFile(file) {
            try {
                const response = await axios.put(`${this.url}/updateFileName/${file.id} `, file.fileName);

                this.newFile = response.data;

                this.newFileSizeInBytes = this.newFile?.fileSize?.replace(' KB', '') * 1000;
                this.loadContactFiles();
                await this.duplicateFileName(response.data);

                if (!this.isDuplicate) {
                    this.$toast({ message: 'Renamed file' });
                }
            } catch (error) {
                if (error.response && error.response.status === 400) {
                    this.$toast({ message: error.response.data });
                    this.loadContactFiles();
                }
            }
        },

        async replaceOldFileWithNew(type) {
            const replaceType = (type === 'replaceFile');
            const replaceObj = {
                replace: replaceType,
                newId: this.newFile.id,
                oldId: this.oldFile.id,
            };

            try {
                await axios.post(`${this.url}/replaceFile`, replaceObj);

                this.$toast({ message: replaceType ? 'File is replaced' : 'Updated file name' });
                this.$refs.fileModal.modalClose();
                this.loading = false;
                this.loadContactFiles();
            } catch (error) {
                this.loading = false;
                this.$toast({ message: replaceType ? 'Error replacing file' : 'Error updating file' });
            }
        },

        sortTable({ ascending }) {
            const factor = ascending === true ? -1 : 1;

            this.sortAscending = ascending;
            this.files = this.files?.sort((prev, next) => {
                if (prev.dateCreated > next.dateCreated) return -1 * factor;
                if (prev.dateCreated < next.dateCreated) return 1 * factor;

                return 0;
            });
        },

        handleSelection({ target }) {
            this.loading = true;

            if (target && target?.files?.length > 0) {
                const [file] = target?.files;

                this.inValidFileSize = false;
                this.uploadFileName = file?.name;
                const size = (file?.size / (1024 * 1024)).toFixed(2);

                if (size > 10) {
                    this.loading = false;
                    this.$toast({ message: 'Files larger than 10MB cannot be uploaded.' });
                    this.inValidFileSize = true;

                    return;
                }

                this.getBase64(file).then((res) => {
                    this.imageSrc = res.split(',').pop();
                    this.fileUpload();
                });
            } else {
                this.resetFields();
            }
        },

        resetFields() {
            this.uploadFileName = '';
            this.imageSrc = '';
            this.loading = false;
        },

        getBase64(file) {
            return new Promise(((resolve, reject) => {
                const reader = new FileReader();
                let img = '';

                reader.readAsDataURL(file);

                reader.onload = function () {
                    img = reader.result;
                };

                reader.onerror = function (error) {
                    reject(error);
                };

                reader.onloadend = function () {
                    resolve(img);
                };
            }));
        },
    },
};
</script>

<style lang="scss" scoped>
.contact-files {
    .files-header {
        display: flex;
        justify-content: space-between;
        padding: $spacing-200 0;
        align-items: center;
    }

    .no-files-section {
        font-size: 1.2rem;
        padding: $spacing-600;
        text-align: center;
        color: $color-gray-darker;
        border-top: 1px solid $color-gray-lighter;
    }

    .center {
        text-align: center;
        margin: 0 auto;
    }

    .delete .icon,
    .trash .icon {
        width: 1.25rem;
        min-width: 1.25rem;
    }

    .delete,
    .trash ,
    .edit-icon,
    .download-file {
        cursor: pointer;
    }

    .edit-icon {
        width: 1rem;
        min-width: 1rem;
        margin-left: $spacing-100;
    }

    .file-name {
        background-color: $color-gray-100;
        border: px-to-rem(1px) solid $color-gray-200;
        padding: .3rem .6rem;
        border-radius: .3rem;
        width: 11rem;
        overflow: hidden;
        height: 2rem;
        line-height: 1.5;
        margin-bottom: 0;
    }

    .new-file-upload {
        display: flex;
        justify-content: flex-end;
    }

    .fileupload-helptext {
        margin: px-to-rem(2px) 0 0;
        font-size: $font-size-xs;
    }

    .filename {
        width: 20rem;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        display: block;
    }
}
</style>
